import { Sparkles as WhyIcon } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// WhyAffordance — tiny "Why?" icon button that reveals one line of AI
// reasoning on click. Phase 3 task 3.3 (visible AI reasoning).
//
// Design calls:
//   * Icon-only trigger so the explanation doesn't crowd the surface it sits
//     next to. The tooltip on the trigger names what it is so the icon is
//     still discoverable.
//   * Popover (not Tooltip) on click so the reasoning is sticky enough to
//     read and copy; hover alone would dismiss too easily.
//   * Renders nothing when the rationale is empty, so callers can pass
//     `rationale` freely without guarding at every call site.
// ---------------------------------------------------------------------------

interface WhyAffordanceProps {
  /** The reasoning line. If empty / whitespace, nothing renders. */
  rationale?: string | null;
  /** Optional secondary line (e.g. strategy link, confidence band). */
  secondary?: string | null;
  /** Button size. Compact is used on dense rows like the PriorityList. */
  size?: 'compact' | 'default';
  /** Aria label. Defaults to "Why this decision?". */
  label?: string;
  /** Popover side override. Defaults to "bottom". */
  side?: 'top' | 'right' | 'bottom' | 'left';
}

export default function WhyAffordance({
  rationale,
  secondary,
  size = 'default',
  label = 'Why this decision?',
  side = 'bottom',
}: WhyAffordanceProps) {
  if (!rationale || rationale.trim().length === 0) return null;

  const buttonClasses = cn(
    'inline-flex items-center justify-center rounded-md text-primary transition-colors',
    'hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
    size === 'compact' ? 'h-5 w-5 p-0.5' : 'h-6 w-6 p-1',
  );
  const iconClasses = size === 'compact' ? 'h-3.5 w-3.5' : 'h-4 w-4';

  return (
    <Popover>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <button type="button" aria-label={label} className={buttonClasses}>
              <WhyIcon className={iconClasses} />
            </button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent>{label}</TooltipContent>
      </Tooltip>
      <PopoverContent
        side={side}
        align="start"
        className="max-w-[360px] border-l-[3px] border-l-primary"
      >
        <p className="px-2 pb-1 text-[10px] font-semibold uppercase tracking-[0.12em] leading-none text-primary">
          AI reasoning
        </p>
        <p className="px-2 whitespace-pre-wrap text-sm">{rationale}</p>
        {secondary && secondary.trim().length > 0 && (
          <p className="mx-2 mt-2 border-t border-border/60 pt-2 text-xs text-muted-foreground">
            {secondary}
          </p>
        )}
      </PopoverContent>
    </Popover>
  );
}
